import React from 'react';
import { Group, Line, Circle } from 'react-konva';
import PropTypes from 'prop-types';

// Constants
import { DEFAULT } from '../constants';
import { DAY, INDICATOR } from '../constants/tokens';

/**
 * Outline widths on screen. They were image pixels, so on a high-resolution
 * floor plan shrunk to fit, an 8px selection outline came out about 1px wide
 * and a selected room looked like any other.
 */
const OUTLINE_PX = 2;
const SELECTED_PX = 4;

/** The hub dot's size on screen, as the design draws it: 12px with a 2px ring. */
const HUB_DOT_PX = 12;
const HUB_RING_PX = 2;

/**
 * Where to put a room's hub dot: the polygon's centroid, falling back to the
 * average of its corners for a degenerate (zero-area) shape.
 */
function centroid(vertices) {
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < vertices.length; i++) {
    const a = vertices[i];
    const b = vertices[(i + 1) % vertices.length];
    const cross = a.x * b.y - b.x * a.y;
    area += cross;
    cx += (a.x + b.x) * cross;
    cy += (a.y + b.y) * cross;
  }
  if (Math.abs(area) < 1e-6) {
    return {
      x: vertices.reduce((s, v) => s + v.x, 0) / vertices.length,
      y: vertices.reduce((s, v) => s + v.y, 0) / vertices.length
    };
  }
  return { x: cx / (3 * area), y: cy / (3 * area) };
}

// Props: vertices, statusColor, isSelected, onClick, blinking, isNormal,
// hubsConnected, mapScale
export default class RoomStatusZone extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      hover: false,
      timerCount: 0
    };
  }

  componentDidMount() {
    // Start the blinking timer
    this.timer = setInterval(
      () => {
        this.setState({ timerCount: this.state.timerCount + 1 });
      },
      500
    );
  }

  componentWillUnmount() {
    // Stop the blinking timer
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  render() {
    // Room.vertices defaults to null, so a room that has never had a zone
    // drawn for it arrives here empty. Render nothing rather than taking the
    // whole map down with it.
    const vertices = this.props.vertices;
    if (vertices == null || vertices.length < 3) return null;

    const points = vertices.reduce((acc, vertex) => acc.concat([vertex.x, vertex.y]), []);
    const color = this.props.statusColor ?? DEFAULT;
    const emphasised = this.state.hover || this.props.isSelected;

    // v7 outlines every zone, not just the hovered one, so room shapes stay
    // readable against a busy floor plan. A Normal room is ringed in green, as
    // the design draws it, so "fine" reads as a positive state rather than as
    // no colour at all. Hover and selection thicken the line and take over its
    // colour.
    // The selected room is outlined in ink, whatever its status: a darker
    // shade of grey or green on a Normal room did not read as selected.
    let stroke = this.props.isNormal ? INDICATOR.success : color.main;
    if (this.props.isSelected) stroke = DAY.ink;
    else if (emphasised && this.props.onClick) stroke = color.dark;

    // Screen pixels to image pixels, so outlines, the dot and its ring stay
    // the same size whatever the floor plan's resolution and zoom.
    const scale = this.props.mapScale > 0 ? this.props.mapScale : 1;
    const dot = this.props.hubsConnected == null ? null : centroid(vertices);

    return (
      <Group>
        <Line
          points={points}
          fill={color.main}
          opacity={
            this.state.hover ? (
              this.props.isSelected ? 0.8 : 0.6
            ) : (
              this.props.blinking && this.state.timerCount % 2 === 0 ? 0.7 : (
                this.props.isSelected ? 0.9 : 0.5
              )
            )
          }
          closed
          stroke={stroke}
          strokeWidth={(emphasised ? SELECTED_PX : OUTLINE_PX) / scale}
          onClick={this.props.onClick ? this.props.onClick : null}
          onMouseenter={() => this.setState({ hover: true })}
          onMouseleave={() => this.setState({ hover: false })}
        />
        {/* Hub connection: green when every hub in the room is reporting,
            grey when one is not. Not listening, so a click on it selects the
            room underneath. Real hex, because canvas cannot read the CSS
            variables the rest of the theme uses. */}
        {dot &&
          <Circle
            x={dot.x}
            y={dot.y}
            radius={HUB_DOT_PX / 2 / scale}
            fill={this.props.hubsConnected ? INDICATOR.success : DAY.mutedSoft}
            stroke='#ffffff'
            strokeWidth={HUB_RING_PX / scale}
            listening={false}
          />}
      </Group>
    );
  }
}

RoomStatusZone.propTypes = {
  vertices: PropTypes.arrayOf(PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  })),
  statusColor: PropTypes.object,
  blinking: PropTypes.bool,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  // The room is Normal: ring it in green.
  isNormal: PropTypes.bool,
  // Whether every hub in the room is connected; null or absent for a room
  // without hubs, which gets no dot.
  hubsConnected: PropTypes.bool,
  // Screen pixels per image pixel, from MapPanel.
  mapScale: PropTypes.number
};
